import React, { useEffect, useState } from 'react';
import { collectDocImageFileIds, applyResolvedUrlsToDoc } from '@odo/shared-ui';
import type { TiptapDoc as SharedTiptapDoc } from '@odo/shared-ui';
import { resolveInternalImageUrls } from '../services/patient/internalImages';

// ---------------------------------------------------------------------------
// Minimal local types — no dependency on files-viewer types
// ---------------------------------------------------------------------------

interface TiptapNode {
    type?: string;
    attrs?: Record<string, unknown>;
    content?: TiptapNode[];
    marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
    text?: string;
}

export interface TiptapDoc {
    type: 'doc';
    content?: TiptapNode[];
}

interface TiptapContentProps {
    doc: unknown;
    patientId: string;
}

// ---------------------------------------------------------------------------
// URL safety — only allow http(s) for link hrefs and image srcs, so a
// javascript:/data: URL in a notesDoc can't render into a clickable/loadable
// attribute. Anything else resolves to '' (link/image is dropped).
// ---------------------------------------------------------------------------

function safeUrl(raw: unknown): string {
    return typeof raw === 'string' && /^https?:\/\//i.test(raw) ? raw : '';
}

// ---------------------------------------------------------------------------
// Inline mark renderer
// ---------------------------------------------------------------------------

function applyMarks(
    children: React.ReactNode,
    marks: Array<{ type: string; attrs?: Record<string, unknown> }> | undefined,
    key: string | number
): React.ReactElement {
    if (!marks || marks.length === 0) {
        return <React.Fragment key={key}>{children}</React.Fragment>;
    }

    let node: React.ReactElement = <React.Fragment key={key}>{children}</React.Fragment>;

    for (const mark of marks) {
        switch (mark.type) {
            case 'bold':
            case 'strong':
                node = <strong key={key}>{node}</strong>;
                break;
            case 'italic':
            case 'em':
                node = <em key={key}>{node}</em>;
                break;
            case 'underline':
                node = <u key={key}>{node}</u>;
                break;
            case 'strike':
                node = <s key={key}>{node}</s>;
                break;
            case 'code':
                node = (
                    <code
                        key={key}
                        className="bg-gray-100 text-gray-700 rounded px-1 text-xs font-mono"
                    >
                        {node}
                    </code>
                );
                break;
            case 'link': {
                const href = safeUrl(mark.attrs?.href);
                node = href ? (
                    <a
                        key={key}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                    >
                        {node}
                    </a>
                ) : (
                    node
                );
                break;
            }
            default:
                break;
        }
    }

    return node;
}

function renderInline(content: TiptapNode[] | undefined): React.ReactNode {
    if (!content || content.length === 0) return null;
    return content.map((node, i) => {
        if (node.type === 'text' && typeof node.text === 'string') {
            return applyMarks(node.text, node.marks, i);
        }
        if (node.type === 'hardBreak') {
            return <br key={i} />;
        }
        return null;
    });
}

// ---------------------------------------------------------------------------
// Block node renderer
// ---------------------------------------------------------------------------

function renderImageGrid(node: TiptapNode, keyPrefix: string): React.ReactElement {
    const cols = typeof node.attrs?.cols === 'number' ? node.attrs.cols : 2;
    const rowHeight = typeof node.attrs?.rowHeight === 'number' ? node.attrs.rowHeight : 180;
    // Read-only display: only the filled cells are shown — no gray placeholder
    // boxes for empty cells and no letterbox background around the image.
    const filled = (node.content ?? []).filter((cell) => safeUrl(cell.attrs?.src));

    if (filled.length === 0) return <React.Fragment key={keyPrefix} />;

    const gridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(cols, filled.length)}, minmax(0, 1fr))`,
        gap: 8,
        marginTop: 8,
        marginBottom: 8
    };

    return (
        <div key={keyPrefix} style={gridStyle}>
            {filled.map((cell, i) => (
                <img
                    key={`${keyPrefix}:cell:${i}`}
                    src={safeUrl(cell.attrs?.src)}
                    alt=""
                    className="object-contain rounded"
                    style={{ maxHeight: rowHeight, maxWidth: '100%' }}
                />
            ))}
        </div>
    );
}

function renderBlock(node: TiptapNode, keyPrefix: string): React.ReactNode {
    switch (node.type) {
        case 'doc':
            return renderNodes(node.content, keyPrefix);

        case 'paragraph':
            return (
                <p key={keyPrefix} className="text-sm text-gray-700 leading-relaxed min-h-[1em]">
                    {renderInline(node.content) ?? ' '}
                </p>
            );

        case 'heading': {
            const level = Number(node.attrs?.level ?? 3);
            const cls =
                level <= 2
                    ? 'text-base font-bold text-gray-800'
                    : 'text-sm font-bold text-gray-800';
            return (
                <div key={keyPrefix} className={cls}>
                    {renderInline(node.content)}
                </div>
            );
        }

        case 'bulletList':
            return (
                <ul key={keyPrefix} className="list-disc pl-5 space-y-0.5 text-sm text-gray-700">
                    {(node.content ?? []).map((li, i) => renderBlock(li, `${keyPrefix}:li:${i}`))}
                </ul>
            );

        case 'orderedList': {
            const start = Number(node.attrs?.start ?? 1);
            return (
                <ol
                    key={keyPrefix}
                    className="list-decimal pl-5 space-y-0.5 text-sm text-gray-700"
                    start={start}
                >
                    {(node.content ?? []).map((li, i) => renderBlock(li, `${keyPrefix}:li:${i}`))}
                </ol>
            );
        }

        case 'listItem':
            return <li key={keyPrefix}>{renderNodes(node.content, keyPrefix)}</li>;

        case 'blockquote':
            return (
                <blockquote
                    key={keyPrefix}
                    className="border-l-4 border-gray-300 pl-3 text-sm text-gray-600 italic my-1"
                >
                    {renderNodes(node.content, keyPrefix)}
                </blockquote>
            );

        case 'horizontalRule':
            return <hr key={keyPrefix} className="border-gray-200 my-2" />;

        case 'hardBreak':
            return <br key={keyPrefix} />;

        case 'text':
            return applyMarks(node.text ?? '', node.marks, keyPrefix);

        case 'image': {
            const src = safeUrl(node.attrs?.src);
            const alt = typeof node.attrs?.alt === 'string' ? node.attrs.alt : '';
            return src ? (
                <img key={keyPrefix} src={src} alt={alt} className="max-w-full rounded my-1" />
            ) : null;
        }

        case 'imageGrid':
            return renderImageGrid(node, keyPrefix);

        default:
            // Unknown node — try to render children gracefully, never throw
            if (Array.isArray(node.content) && node.content.length > 0) {
                return (
                    <React.Fragment key={keyPrefix}>
                        {renderNodes(node.content, keyPrefix)}
                    </React.Fragment>
                );
            }
            return null;
    }
}

function renderNodes(nodes: TiptapNode[] | undefined, keyPrefix: string): React.ReactNode {
    if (!nodes || nodes.length === 0) return null;
    return nodes.map((node, i) => renderBlock(node, `${keyPrefix}:${i}`));
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Renders a TipTap doc JSON as styled React/HTML. Backward-compatible: if
 * `doc` is not a valid TipTap doc object the component renders nothing.
 * On mount, freshens stale GCS image URLs via the backend resolve endpoint.
 */
const TiptapContent: React.FC<TiptapContentProps> = ({ doc, patientId }) => {
    const [resolvedDoc, setResolvedDoc] = useState<TiptapDoc | null>(null);

    useEffect(() => {
        if (!doc || typeof doc !== 'object') return;
        const raw = doc as TiptapDoc;
        if (raw.type !== 'doc') return;

        const sharedDoc: SharedTiptapDoc = { type: 'doc', content: raw.content ?? [] };
        const fileIds = collectDocImageFileIds(sharedDoc);

        if (fileIds.length === 0) {
            setResolvedDoc(raw);
            return;
        }

        let cancelled = false;
        resolveInternalImageUrls(patientId, fileIds)
            .then((urls) => {
                if (cancelled) return;
                if (Object.keys(urls).length === 0) {
                    setResolvedDoc(raw);
                } else {
                    const resolved = applyResolvedUrlsToDoc(sharedDoc, urls);
                    setResolvedDoc({
                        ...raw,
                        content: resolved.content as TiptapDoc['content']
                    });
                }
            })
            .catch(() => {
                // Leave unresolved — images may render broken, but text stays intact.
                if (!cancelled) setResolvedDoc(raw);
            });

        return () => {
            cancelled = true;
        };
    }, [doc, patientId]);

    if (!resolvedDoc) {
        if (!doc || typeof doc !== 'object') return null;
        const raw = doc as TiptapDoc;
        if (raw.type !== 'doc') return null;
        // Render without resolved URLs while async resolution is in flight
        return (
            <div className="space-y-1.5">
                {renderNodes(raw.content as TiptapNode[] | undefined, 'root')}
            </div>
        );
    }

    return (
        <div className="space-y-1.5">
            {renderNodes(resolvedDoc.content as TiptapNode[] | undefined, 'root')}
        </div>
    );
};

export default TiptapContent;
